import { Injectable, Optional } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LEGACY_AUTH_REQUIRED_KEY } from '@models/consts/storage-constants.model';

/**
 * Guard that prevents access to login page when MSAL is enabled.
 * Redirects to SSO or home page instead.
 */
@Injectable()
export class MsalRedirectGuard implements CanActivate {

    constructor(
        private router: Router,
        @Optional() private msalService: MsalService,
        @Optional() private msalBroadcastService: MsalBroadcastService
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        // If MSAL is not enabled, allow access to login page
        if (!environment.msalEnable) {
            return true;
        }

        // Allow legacy authentication for users without SSO access
        if (route.queryParams['legacyAuth'] === 'true' ||
            sessionStorage.getItem(LEGACY_AUTH_REQUIRED_KEY) === 'true') {
            return true;
        }

        // Allow access to login page when boschId is present (badge scan flow)
        if (route.queryParams['boschId']) {
            return true;
        }

        // If MSAL services are not available, allow access to login page
        if (!this.msalService || !this.msalBroadcastService) {
            return true;
        }

        // Wait for any in-progress interaction to complete
        return this.msalBroadcastService.inProgress$.pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            take(1),
            switchMap(() => {
                const accounts = this.msalService.instance.getAllAccounts();

                // If user is already authenticated with MSAL, redirect to home
                if (accounts.length > 0) {
                    this.router.navigate(['/']);
                    return of(false);
                }

                // No accounts - trigger MSAL login redirect
                const scopes = environment.msalModuleConfig.scopes ? [environment.msalModuleConfig.scopes] : [];
                this.msalService.loginRedirect({ scopes, prompt: 'select_account' });
                return of(false);
            })
        );
    }
}



